import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Divider,
  Flex,
  GridItem,
  Image,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import {
  getPrettyAircraftType,
  interleave,
  modelUtils,
  prettyCurrency,
  prettyNumber,
  prettyStopCount,
  RoundingModeOptions,
} from '@pelicargo/utils';
import { AircraftType, LineItemType, VariantType } from '@prisma/client';
import { useEffect, useMemo } from 'react';
import { HiChevronRight } from 'react-icons/hi2';
import { Link as RRLink, useNavigate } from 'react-router-dom';

import { trpc } from '../../config/trpc';
import { goToQuote, useQuoteHelper } from '../../hooks/quotes/useQuoteHelper';
import { useQuoteVariantHelper } from '../../hooks/useQuoteVariantHelper';
import { useSelectedQuoteVariant } from '../../hooks/useSelectedQuoteVariant';
import { API } from '../../utils/apiTypes';
import { getBestQuoteVariants } from '../../utils/getTotalCost';
import { AirportStops } from '../AirportStops';
import { ConditionalTags } from '../ConditionalTags';
import { FlightSegmentsCard } from '../FlightSegmentsCard';
import { IconPivotWeight } from '../Icons/IconPivotWeight';
import { RightAlignedLabeledValue } from '../LabeledValue';
import { QuoteVariantTabs } from '../QuoteVariantTabs';

type Props = {
  quote: API.QuoteWithTotalCost;
  selectedCargoType?: VariantType;
  requestPickupOrigin?: string;
  requestChargeableWeight?: number;
};

const FLIGHT_DETAIL_SKELETON_ROWS = [1, 2, 3, 4];
const LINE_ITEM_SKELETON_ROWS = [1, 2, 3, 4, 5];

export const QuoteListItem = ({
  quote,
  selectedCargoType,
  requestPickupOrigin,
  requestChargeableWeight,
}: Props) => {
  const { selectedQuoteVariantId, setSelectedQuoteVariantId } = useSelectedQuoteVariant();

  const bestQuoteVariants = getBestQuoteVariants(quote?.quote_variants || [], selectedQuoteVariantId);
  const cheapestQuoteVariant = bestQuoteVariants?.[0];
  const quoteVariantByType = quote?.quote_variants?.find((variant) => variant.type === selectedCargoType);
  const selectedQuoteVariant = bestQuoteVariants?.find((variant) => variant.id === selectedQuoteVariantId);
  const quoteVariant = selectedQuoteVariant || quoteVariantByType || cheapestQuoteVariant;

  useEffect(() => {
    if (quoteVariant?.id) {
      setSelectedQuoteVariantId(quoteVariant.id);
    }
  }, [quoteVariant?.id, setSelectedQuoteVariantId]);

  const navigate = useNavigate();

  const pickupOrigin = requestPickupOrigin;

  const quoteHelper = useQuoteHelper(quote, { requestChargeableWeight });
  const quoteVariantHelper = useQuoteVariantHelper(quoteVariant as any, quote);

  const { isBookableStage, isExpired, stopCount, mostConstrainedLeg } = quoteHelper;
  const {
    hasInlandService,
    exclusiveVariableRate,
    allInVariableRate,
    airFreightAllInVariableRate,
    inlandAllInVariableRate,

    screeningRate,
    otherFeesTotal,
    airFreightTotal,
    inlandTotal,
    totalCost,
  } = quoteVariantHelper;

  const isScreeningRatePerUnit = useMemo(() => {
    return quoteVariant?.line_items?.some((item) => item.type === LineItemType.SCREENING && !!item.per_unit_cost);
  }, [quoteVariant?.line_items]);

  const expansionDisclosure = useDisclosure();
  // Expanded details are intentionally loaded on demand to keep the list shell fast.
  const shouldLoadExpandedQuote = expansionDisclosure.isOpen && isBookableStage;
  const { data: expandedQuote, isFetching: isExpandedQuoteFetching } = trpc.getQuote.useQuery(
    { id: quote.id },
    { enabled: shouldLoadExpandedQuote, refetchOnWindowFocus: false },
  );

  // RESPONSIVE BREAK POINTS
  const isHeaderFlightInfoVisible = useBreakpointValue({ base: false, sm: true });
  const isVerticalDividerVisible = useBreakpointValue({ base: !isBookableStage, sm: true });
  const isCardLink = useBreakpointValue({ base: true, sm: false });

  // EVENT HANDLERS
  const navigateToBooking = () => {
    navigate(goToQuote(quote, { id: selectedQuoteVariantId }));
  };

  const handleCardClick = () => {
    if (isCardLink) return navigateToBooking();

    return expansionDisclosure.onToggle();
  };

  const visibleAircraftType = useMemo(() => {
    if (quote?.flight_path?.some((node) => node.aircraft_type === AircraftType.RFS)) {
      return getPrettyAircraftType(AircraftType.RFS);
    }

    return getPrettyAircraftType(mostConstrainedLeg?.aircraft_type);
  }, [mostConstrainedLeg?.aircraft_type, quote?.flight_path]);

  const FeeBreakdown = () => {
    const hasInland = !!quoteVariant?.inland_service_quote_variant;

    if (hasInland) {
      return (
        <>
          <RightAlignedLabeledValue
            label={`${prettyCurrency(airFreightTotal)} airline`}
            value={`${prettyCurrency(airFreightAllInVariableRate)}/kg`}
          />
          <RightAlignedLabeledValue
            label={`${prettyCurrency(inlandTotal)} inland`}
            value={`${prettyCurrency(inlandAllInVariableRate)}/kg`}
          />
        </>
      );
    }

    const screeningCharge = screeningRate
      ? `+${prettyCurrency(screeningRate)}${isScreeningRatePerUnit ? '/kg' : ''} scrn`
      : 'air freight';

    const isPivotWeight = !!requestChargeableWeight && requestChargeableWeight !== quote?.chargeable_weight;

    const maybePivotWeight = isPivotWeight ? (
      <Stack direction="row" align="center">
        <IconPivotWeight />
        <Text>pivot weight</Text>
      </Stack>
    ) : (
      'charge weight'
    );

    return (
      <>
        <RightAlignedLabeledValue
          label={maybePivotWeight}
          value={`${prettyNumber(quote.chargeable_weight, { roundingMode: RoundingModeOptions.Ceil })}kg`}
        />
        <RightAlignedLabeledValue label={screeningCharge} value={`${prettyCurrency(exclusiveVariableRate)}/kg`} />
        <Show if={!!otherFeesTotal && otherFeesTotal !== totalCost}>
          <RightAlignedLabeledValue label="other fees" value={prettyCurrency(otherFeesTotal)} />
        </Show>
      </>
    );
  };

  const { data: backlogData, refetch } = trpc.isQuoteBacklogged.useQuery({ id: quote.id });

  const airports = useMemo(() => modelUtils.quote.relevantBacklogAirports(quote), [quote]);

  trpc.onBacklogUpdate.useSubscription({ provider_id: quote.provider_id, airports }, { onData: () => refetch() });

  const warnings: string[] = [];
  if (isExpired) warnings.push('Expired');

  return (
    <Card
      p={0}
      borderRadius="lg"
      transition="border-color 200ms ease"
      overflow="hidden"
      borderColor="border.default"
      _hover={{
        borderWidth: 1,
        borderColor: isBookableStage ? 'border.hover' : 'border.default',
        overflow: 'hidden',
      }}
    >
      <CardBody
        p={0}
        m={0}
        borderRadius="lg"
        cursor={isBookableStage ? 'pointer' : 'initial'}
        onClick={handleCardClick}
      >
        <Stack
          direction={{ base: 'column', sm: 'row' }}
          spacing={0}
          p={0}
          divider={!isVerticalDividerVisible && <Divider />}
        >
          <Stack
            spacing={1.5}
            py={0}
            px={4}
            borderTopLeftRadius="lg"
            borderRightWidth={isBookableStage ? 1 : 0}
            borderBottomLeftRadius="lg"
            justify="space-between"
          >
            <Stack direction="row" align="center" minH={6}>
              <ConditionalTags quote={quote} quoteVariant={quoteVariant} backlogs={backlogData?.backlogs} />
            </Stack>
            <Stack
              direction="row"
              justify={{ base: 'space-between', md: 'left' }}
              align="bottom"
              wrap="wrap"
              spacing={{ base: '4', lg: '8' }}
            >
              <Skeleton isLoaded={!!quote?.provider}>
                <Image src={quote?.provider?.logo_uri} height="40px" width="40px" alt={quote?.provider?.name} />
              </Skeleton>

              <Stack direction="row" w="280px">
                <AirportStops flightSegments={quote.flight_path as any} pickupOrigin={pickupOrigin} w="full" />
              </Stack>

              <Show if={isHeaderFlightInfoVisible}>
                <SimpleGrid columns={3} columnGap={4} alignItems="center">
                  <GridItem colSpan={1} height="fit-content" minW={24}>
                    <Text textStyle="bodySmallRegular">{prettyStopCount(stopCount)}</Text>
                  </GridItem>
                  <Show if={!!quote?.estimated_transit_time}>
                    <GridItem colSpan={1} height="fit-content" minW={24}>
                      <Text textStyle="bodySmallRegular">{quote?.estimated_transit_time}</Text>
                    </GridItem>
                  </Show>
                  <GridItem colSpan={1} height="fit-content" minW={24}>
                    <Text textStyle="bodySmallRegular">{visibleAircraftType}</Text>
                  </GridItem>
                </SimpleGrid>
              </Show>
            </Stack>
            <Stack direction="row" align="center" minH={6}>
              <Skeleton isLoaded={!!quote?.provider} noOfLines={1} w={!quote?.provider ? 20 : 'full'}>
                <Text textStyle="captionSmallRegular" color="text.subtle">
                  {quote?.provider?.name}
                </Text>
              </Skeleton>
            </Stack>
          </Stack>
          <Stack
            justify="space-between"
            spacing={1.5}
            px={4}
            flex={1}
            backgroundColor="bg.surface !important"
            _hover={{ backgroundColor: 'bg.surface !important' }}
          >
            <Show if={isBookableStage}>
              <Stack
                direction={{ base: 'column', lg: 'row' }}
                divider={<Divider orientation="vertical" />}
                h="full"
                w="full"
                justify="space-between"
                gap={{ base: '1', sm: '3' }}
              >
                <Flex flexDir="column" justifyContent="center">
                  <Stack
                    direction="row"
                    align="center"
                    justify="flex-end"
                    spacing={4}
                    minW={{ base: 'unset', sm: 'xs', md: 'sm', lg: 'sm' }}
                    wrap="wrap"
                  >
                    <FeeBreakdown />
                  </Stack>
                  <Flex direction="row" marginLeft="auto" gap="1">
                    {interleave(
                      warnings.map((warning) => (
                        <Text textStyle="captionSmallMedium" color="error" key={warning}>
                          {warning}
                        </Text>
                      )),
                      <Text textStyle="captionSmallMedium" color="error">
                        •
                      </Text>,
                    )}
                  </Flex>
                </Flex>
                <Stack direction="column" spacing="1" h="full" justify="center" py={3}>
                  <Stack direction="column" spacing={0}>
                    <Show if={hasInlandService}>
                      <Stack direction="row" align="baseline" spacing="1">
                        <Text fontSize="2xl" fontWeight="500">
                          {prettyCurrency(allInVariableRate)}
                        </Text>
                        <Text textStyle="bodySmallMedium" color="text.muted">
                          /kg
                        </Text>
                      </Stack>
                      <Stack direction="row" align="baseline" spacing="1" mt={-0.5}>
                        <Text fontSize="sm" fontWeight="500">
                          {prettyCurrency(totalCost)}
                        </Text>
                        <Text fontSize="xs" color="text.muted">
                          total
                        </Text>
                      </Stack>
                    </Show>
                    <Show if={!hasInlandService}>
                      <Stack direction="row" align="end" spacing="1" justifySelf="center">
                        <Text fontSize="2xl" fontWeight="500">
                          {prettyCurrency(totalCost)}
                        </Text>
                        <Text textStyle="bodySmallMedium" color="text.muted">
                          total
                        </Text>
                      </Stack>
                    </Show>
                  </Stack>

                  <Button
                    as={RRLink}
                    to={goToQuote(quote, { id: selectedQuoteVariantId })}
                    colorScheme="primary"
                    size="sm"
                    iconSpacing="2"
                    minW="113px"
                    rightIcon={<HiChevronRight strokeWidth="2" />}
                  >
                    View Quote
                  </Button>
                </Stack>
              </Stack>
            </Show>
          </Stack>
        </Stack>
      </CardBody>
      <Show if={expansionDisclosure.isOpen && isBookableStage}>
        <CardFooter
          bg="bg.subtle"
          display={{ base: 'none', md: 'flex' }}
          p="3"
          borderBottomStartRadius="lg"
          borderBottomEndRadius="lg"
        >
          <Stack
            direction={{ base: 'column', xl: 'row' }}
            align="center"
            justify="flex-start"
            w="full"
            p="0"
            spacing="3"
          >
            <Show if={!!expandedQuote}>
              <>
                <FlightSegmentsCard
                  flightSegments={expandedQuote?.flight_path}
                  estimatedTransitTime={expandedQuote?.estimated_transit_time}
                  quote={expandedQuote}
                  pickupOrigin={pickupOrigin}
                  originAirportId={expandedQuote?.origin_airport_id}
                  destinationAirportId={expandedQuote?.destination_airport_id}
                  h="full"
                  flex={1}
                />
                <Card flex={1} h="full">
                  <CardBody p="0">
                    <QuoteVariantTabs quote={expandedQuote} quoteVariants={expandedQuote?.quote_variants || []} />
                  </CardBody>
                </Card>
              </>
            </Show>
            <Show if={!expandedQuote}>
              <>
                <Card flex={1} h="full" px="4" py="3">
                  <CardBody p="0">
                    <Stack spacing="6">
                      <Stack direction="row" justify="space-between" align="center">
                        <Skeleton isLoaded={!isExpandedQuoteFetching} h="22px" w="220px" />
                        <Skeleton isLoaded={!isExpandedQuoteFetching} h="18px" w="56px" />
                      </Stack>
                      <Stack spacing="4">
                        {FLIGHT_DETAIL_SKELETON_ROWS.map((row) => (
                          <Stack key={row} direction="row" align="center" justify="space-between" spacing="4">
                            <Skeleton isLoaded={!isExpandedQuoteFetching} h="16px" w="180px" />
                            <Skeleton isLoaded={!isExpandedQuoteFetching} h="14px" w="48px" />
                          </Stack>
                        ))}
                      </Stack>
                      <Stack align="flex-end">
                        <Skeleton isLoaded={!isExpandedQuoteFetching} h="14px" w="150px" />
                      </Stack>
                    </Stack>
                  </CardBody>
                </Card>

                <Card flex={1} h="full">
                  <CardBody p="0">
                    <Stack spacing="0">
                      <Stack direction="row" px="4" py="3" spacing="3">
                        <Skeleton isLoaded={!isExpandedQuoteFetching} h="20px" flex="1" />
                        <Skeleton isLoaded={!isExpandedQuoteFetching} h="20px" flex="1" />
                      </Stack>
                      <Divider />
                      <Stack px="4" py="3" spacing="3">
                        <Skeleton isLoaded={!isExpandedQuoteFetching} h="16px" w="120px" />
                        {LINE_ITEM_SKELETON_ROWS.map((row) => (
                          <Stack key={row} direction="row" align="center" justify="space-between" spacing="4">
                            <Skeleton isLoaded={!isExpandedQuoteFetching} h="14px" w="220px" />
                            <Skeleton isLoaded={!isExpandedQuoteFetching} h="14px" w="96px" />
                            <Skeleton isLoaded={!isExpandedQuoteFetching} h="14px" w="88px" />
                          </Stack>
                        ))}
                        <Divider />
                        <Stack direction="row" align="center" justify="space-between">
                          <Skeleton isLoaded={!isExpandedQuoteFetching} h="24px" w="72px" />
                          <Stack direction="row" spacing="4">
                            <Skeleton isLoaded={!isExpandedQuoteFetching} h="24px" w="90px" />
                            <Skeleton isLoaded={!isExpandedQuoteFetching} h="24px" w="90px" />
                          </Stack>
                        </Stack>
                        <Stack align="flex-end" pt="1">
                          <Skeleton isLoaded={!isExpandedQuoteFetching} h="36px" w="120px" />
                        </Stack>
                      </Stack>
                    </Stack>
                  </CardBody>
                </Card>
              </>
            </Show>
          </Stack>
        </CardFooter>
      </Show>
    </Card>
  );
};
